import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Search,
  ChevronDown,
  Bell,
  MessageSquare,
  User,
  Settings,
  LogOut,
} from 'lucide-react';
import { Avatar } from '../ui/Avatar';
import { ConfirmModal } from '../ui/Modal';
import { getRoleTheme, type BackofficeUserType } from '@/backoffice/config/theme';
import { getDashboardBase } from '@/backoffice/config/navigation';
import { logout } from '@/domains/auth/services/auth.service';
import {
  fetchNotifications,
  markNotificationAsRead,
  type Notification,
} from '@/domains/client/services/client.service';
import { markUserNotificationRead } from '@/domains/api/notifications.api';
import { useUserNotifications } from '@/backoffice/hooks/useUserNotifications';
import { formatRelativeTime } from '@/backoffice/utils';
import { cn } from '@/utils/cn';

interface HeaderProps {
  userType: BackofficeUserType;
  title?: string;
  userName?: string;
  userAvatar?: string;
  showSearch?: boolean;
  onSearch?: (query: string) => void;
}

type HeaderNotification = {
  id: string | number;
  titre: string;
  message: string;
  est_lu: boolean;
  created_at: string;
  reference_id?: number | null;
  source: 'client' | 'platform';
  type?: string;
};

const PLATFORM_NOTIFICATION_ROLES: BackofficeUserType[] = ['pro_driver', 'community_driver', 'relay_point'];

export function Header({
  userType,
  title,
  userName,
  userAvatar,
  showSearch = false,
}: HeaderProps) {
  const navigate = useNavigate();
  const theme = getRoleTheme(userType);
  const dashboardBase = getDashboardBase(userType);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [loggingOut, setLoggingOut] = useState(false);
  const [clientNotifications, setClientNotifications] = useState<Notification[]>([]);
  const usePlatformNotifications = PLATFORM_NOTIFICATION_ROLES.includes(userType);
  const { notifications: platformNotifications, unreadCount: platformUnread, live } = useUserNotifications(
    30000,
    usePlatformNotifications,
  );

  const notifications: HeaderNotification[] = usePlatformNotifications
    ? platformNotifications.map((n) => ({
        id: n.id,
        titre: n.title,
        message: n.body,
        est_lu: n.is_read,
        created_at: n.created_at ?? '',
        reference_id: typeof n.data?.order_request_id === 'number' ? n.data.order_request_id : null,
        source: 'platform' as const,
      }))
    : clientNotifications.map((n) => ({
        id: n.id,
        titre: n.titre,
        message: n.message,
        est_lu: n.est_lu,
        created_at: n.created_at,
        reference_id: n.reference_id,
        source: 'client' as const,
        type: n.type,
      }));

  const unreadCount = usePlatformNotifications
    ? platformUnread
    : clientNotifications.filter((n) => !n.est_lu).length;
  const preview = notifications.slice(0, 5);

  useEffect(() => {
    if (userType !== 'client') return;
    fetchNotifications()
      .then(setClientNotifications)
      .catch(() => setClientNotifications([]));
  }, [userType]);

  const handleLogout = async () => {
    setLoggingOut(true);
    try {
      await logout();
      setShowLogoutConfirm(false);
      setShowUserMenu(false);
      navigate('/login');
    } finally {
      setLoggingOut(false);
    }
  };

  const handleNotificationClick = async (notification: HeaderNotification) => {
    if (!notification.est_lu) {
      try {
        if (notification.source === 'client') {
          await markNotificationAsRead(String(notification.id));
          setClientNotifications((prev) =>
            prev.map((n) => (n.id === notification.id ? { ...n, est_lu: true } : n)),
          );
        } else {
          await markUserNotificationRead(Number(notification.id));
        }
      } catch {
        // ignore
      }
    }
    setShowNotifications(false);
    if (notification.reference_id) {
      if (userType === 'client') {
        const path =
          notification.type === 'payment'
            ? `${dashboardBase}/commandes/${notification.reference_id}/paiement`
            : `${dashboardBase}/commandes/${notification.reference_id}`;
        navigate(path);
      } else if (userType === 'pro_driver' || userType === 'community_driver') {
        navigate(`${dashboardBase}/missions`);
      } else {
        navigate(`${dashboardBase}/notifications`);
      }
    } else {
      navigate(`${dashboardBase}/notifications`);
    }
  };

  return (
    <header className="bg-white border-b border-gray-200 px-4 lg:px-6 py-4 sticky top-0 z-30">
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-4 flex-1">
          {title && (
            <h1 className="text-xl lg:text-2xl font-bold text-gray-900 hidden md:block">
              {title}
            </h1>
          )}

          {showSearch && (
            <div className="relative max-w-md flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Rechercher..."
                className={cn(
                  'w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:border-transparent',
                  theme.focusRing,
                )}
              />
            </div>
          )}
        </div>

        <div className="flex items-center gap-2 lg:gap-4">
          <div className="relative">
            <button
              type="button"
              onClick={() => setShowNotifications(!showNotifications)}
              className="p-2 rounded-lg hover:bg-gray-100 relative"
            >
              <Bell className="w-5 h-5 text-gray-600" />
              {unreadCount > 0 && (
                <span className="absolute top-1 right-1 min-w-[8px] h-2 px-0.5 bg-red-500 rounded-full" />
              )}
            </button>

            {showNotifications && (
              <>
                <div
                  className="fixed inset-0 z-40"
                  onClick={() => setShowNotifications(false)}
                />
                <div className="absolute right-0 top-full mt-2 w-80 bg-white rounded-lg shadow-xl border border-gray-200 z-50 max-h-96 overflow-hidden">
                  <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between gap-2">
                    <h3 className="font-semibold text-gray-900">Notifications</h3>
                    {live && usePlatformNotifications && (
                      <span className="text-xs text-emerald-600">Temps réel</span>
                    )}
                  </div>
                  <div className="divide-y divide-gray-100 max-h-72 overflow-y-auto">
                    {preview.length === 0 ? (
                      <p className="px-4 py-6 text-sm text-gray-500 text-center">
                        Aucune notification
                      </p>
                    ) : (
                      preview.map((notification) => (
                        <button
                          key={notification.id}
                          type="button"
                          onClick={() => handleNotificationClick(notification)}
                          className={cn(
                            'w-full px-4 py-3 hover:bg-gray-50 cursor-pointer text-left',
                            !notification.est_lu && 'bg-teal-50/40',
                          )}
                        >
                          <p className="text-sm font-medium text-gray-900">{notification.titre}</p>
                          <p className="text-xs text-gray-500 mt-1 line-clamp-2">{notification.message}</p>
                          <p className="text-xs text-gray-400 mt-1">
                            {formatRelativeTime(notification.created_at)}
                          </p>
                        </button>
                      ))
                    )}
                  </div>
                  <div className="px-4 py-3 border-t border-gray-100 bg-gray-50">
                    <Link
                      to={`${dashboardBase}/notifications`}
                      className={cn('text-sm font-medium', theme.link, theme.linkHover)}
                      onClick={() => setShowNotifications(false)}
                    >
                      Voir toutes les notifications
                    </Link>
                  </div>
                </div>
              </>
            )}
          </div>

          <button
            type="button"
            className="p-2 rounded-lg hover:bg-gray-100 relative hidden sm:block"
          >
            <MessageSquare className="w-5 h-5 text-gray-600" />
            <span className={cn('absolute top-1 right-1 w-2 h-2 rounded-full', theme.activeIcon.replace('text-', 'bg-'))} />
          </button>

          <div className="relative">
            <button
              type="button"
              onClick={() => setShowUserMenu(!showUserMenu)}
              className="flex items-center gap-2 p-1.5 rounded-lg hover:bg-gray-100"
            >
              <Avatar name={userName} src={userAvatar} size="sm" />
              <span className="hidden lg:block text-sm font-medium text-gray-700 max-w-[120px] truncate">
                {userName}
              </span>
              <ChevronDown className="w-4 h-4 text-gray-400 hidden lg:block" />
            </button>

            {showUserMenu && (
              <>
                <div
                  className="fixed inset-0 z-40"
                  onClick={() => setShowUserMenu(false)}
                />
                <div className="absolute right-0 top-full mt-2 w-56 bg-white rounded-lg shadow-xl border border-gray-200 z-50 overflow-hidden">
                  <div className="px-4 py-3 border-b border-gray-100">
                    <p className="font-medium text-gray-900 truncate">{userName}</p>
                    <p className="text-xs text-gray-500 mt-0.5">{theme.label}</p>
                  </div>
                  <div className="py-2">
                    <Link
                      to={`${dashboardBase}/profil`}
                      className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                      onClick={() => setShowUserMenu(false)}
                    >
                      <User className="w-4 h-4" />
                      Mon profil
                    </Link>
                    <Link
                      to={`${dashboardBase}/parametres`}
                      className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                      onClick={() => setShowUserMenu(false)}
                    >
                      <Settings className="w-4 h-4" />
                      Paramètres
                    </Link>
                  </div>
                  <div className="py-2 border-t border-gray-100">
                    <button
                      type="button"
                      onClick={() => {
                        setShowUserMenu(false);
                        setShowLogoutConfirm(true);
                      }}
                      className="w-full flex items-center gap-2 px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                    >
                      <LogOut className="w-4 h-4" />
                      Déconnexion
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      <ConfirmModal
        isOpen={showLogoutConfirm}
        onClose={() => !loggingOut && setShowLogoutConfirm(false)}
        onConfirm={handleLogout}
        title="Fermer la session ?"
        message="Vous devrez ressaisir vos identifiants Onapila pour vous reconnecter."
        confirmText="Se déconnecter"
        cancelText="Annuler"
        variant="danger"
        isLoading={loggingOut}
      />
    </header>
  );
}
